<template>
    <div class="create-user-dialog">
        <div class="dialog-header">
            <div class="dialog-title">New User</div>
            <div class="dialog-subtitle">Add a user to your organization</div>
        </div>

        <v-form ref="form" @submit.prevent="handleSubmit">
            <v-row dense class="form-content">
                <v-col cols="12">
                    <v-text-field
                        v-model="firstName"
                        label="First Name"
                        variant="outlined"
                        density="comfortable"
                        hide-details="auto"
                        class="input-field"
                        required
                    />
                </v-col>

                <v-col cols="12">
                    <v-text-field
                        v-model="lastName"
                        label="Last Name"
                        variant="outlined"
                        density="comfortable"
                        hide-details="auto"
                        class="input-field"
                        required
                    />
                </v-col>

                <v-col cols="12">
                    <v-text-field
                        v-model="email"
                        label="Email Address"
                        type="email"
                        variant="outlined"
                        density="comfortable"
                        hide-details="auto"
                        :rules="[emailRule]"
                        class="input-field"
                        required
                    />
                </v-col>

                <v-col cols="12">
                    <v-select
                        v-model="newRoleSelectedId"
                        :items="availableRoles"
                        item-title="name"
                        item-value="id"
                        label="Select Role"
                        variant="outlined"
                        density="comfortable"
                        hide-details="auto"
                        class="input-field"
                    />
                </v-col>

                <v-col cols="12" v-if="selectedRole">
                    <div class="role-description-card">
                        <div class="role-description-label">Role Description</div>
                        <div class="role-description-text">
                            {{ selectedRole.description }}
                        </div>
                    </div>
                </v-col>

                <v-col cols="12" v-if="email.trim() && emailRule(email) === true">
                    <div class="helper-text">
                        Invite will be sent to this email address.
                    </div>
                </v-col>

                <v-col cols="12">
                    <div class="actions">
                        <v-btn
                            variant="text"
                            class="cancel-btn"
                            :disabled="isSubmitting"
                            @click="handleCancel"
                        >
                            Cancel
                        </v-btn>

                        <v-btn
                            type="submit"
                            class="create-btn"
                            color="primary"
                            :disabled="!isFormComplete || isSubmitting"
                        >
                            <div class="btn-content">
                                <v-progress-circular
                                    v-if="isSubmitting"
                                    indeterminate
                                    size="18"
                                    width="2"
                                />
                                <span v-else>Create User</span>
                            </div>
                        </v-btn>
                    </div>
                </v-col>
            </v-row>
        </v-form>
    </div>
</template>

<script setup>
import { ref, computed, defineEmits, onMounted } from 'vue'
import AuthZeroService from '@/services/AuthZeroService'
import CreateAuthZeroMemberModel from '@/domain/authorization/CreateAuthZeroUserModel'
import { useOrganizationStore } from '@/store/organizationStore'

const emit = defineEmits(['onUserCreated', 'cancel'])

const availableRoles = ref([])
const newRoleSelectedId = ref(null)
const firstName = ref('')
const lastName = ref('')
const email = ref('')
const isSubmitting = ref(false)
const organizationStore = useOrganizationStore()
const orgId = ref('')
const form = ref(null)

const emailRule = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || 'Invalid email'

const isFormComplete = computed(() => {
    return (
        firstName.value.trim() &&
        lastName.value.trim() &&
        email.value.trim() &&
        newRoleSelectedId.value != null &&
        emailRule(email.value) === true
    )
})

const selectedRole = computed(() => {
    return availableRoles.value.find(r => r.id === newRoleSelectedId.value) || null
})

onMounted(async () => {
    orgId.value = organizationStore.authZeroOrgId || ''
    availableRoles.value = await AuthZeroService.getAssignableRolesAsync()
})

function resetForm() {
    firstName.value = ''
    lastName.value = ''
    email.value = ''
    newRoleSelectedId.value = null
    form.value?.resetValidation?.()
}

function handleCancel() {
    resetForm()
    emit('cancel')
}

async function handleSubmit() {
    if (!isFormComplete.value || isSubmitting.value) return

    isSubmitting.value = true

    try {
        const request = new CreateAuthZeroMemberModel(
            orgId.value,
            email.value.trim(),
            firstName.value.trim(),
            lastName.value.trim(),
            newRoleSelectedId.value,
            organizationStore.organizationId
        )

        const response = await AuthZeroService.createUser(request)

        if (response != null) {
            resetForm()
            emit('onUserCreated')
        }
    } finally {
        isSubmitting.value = false
    }
}
</script>

<style scoped>
.create-user-dialog {
    padding: 12px;
    max-width: 640px;
    margin: 0 auto;
}

.dialog-header {
    margin-bottom: 24px;
}

.dialog-title {
    font-size: 1.35rem;
    font-weight: 700;
    color: rgb(var(--v-theme-on-surface));
    line-height: 1.2;
}

.dialog-subtitle {
    margin-top: 6px;
    font-size: 0.95rem;
    color: rgba(var(--v-theme-on-surface), 0.68);
}

.form-content {
    row-gap: 6px;
}

.input-field {
    width: 100%;
}

.input-field :deep(.v-field) {
    border-radius: 14px;
}

.input-field :deep(.v-field__input) {
    color: rgb(var(--v-theme-on-surface));
}

.input-field :deep(.v-label) {
    color: rgba(var(--v-theme-on-surface), 0.72);
}

.role-description-card {
    width: 100%;
    padding: 14px 16px;
    border-radius: 14px;
    background: rgba(var(--v-theme-on-surface), 0.04);
    border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.role-description-label {
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: rgba(var(--v-theme-on-surface), 0.58);
    margin-bottom: 6px;
}

.role-description-text {
    font-size: 0.95rem;
    line-height: 1.55;
    color: rgba(var(--v-theme-on-surface), 0.88);
    white-space: normal;
    word-break: break-word;
}

.helper-text {
    font-size: 0.85rem;
    color: rgba(var(--v-theme-on-surface), 0.62);
    padding-left: 4px;
    margin-top: -2px;
}

.actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    padding-top: 10px;
}

.cancel-btn {
    color: rgba(var(--v-theme-on-surface), 0.8);
}

.create-btn {
    min-width: 140px;
    border-radius: 12px !important;
    font-weight: 700;
}

.btn-content {
    min-width: 100px;
    min-height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>